import { Component, OnInit, Input, OnChanges, SimpleChanges, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { cloneDeep } from 'lodash';
import { PermissionHandlerService } from '../../services/permission-handler.service';
import { GlobalConstants } from '../../services/global-constants';
import { QuestionBank } from 'src/app/misc/data/questionBank';
import { DataService } from '../../services/data.service';
import { MessageService } from 'primeng/api';
import { TranslatorPipe } from '../../services/translator.pipe';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { OrgUnitsService } from '../../services/org-units.service';

@Component({
  selector: 'app-permission-requests-form',
  templateUrl: './permission-request-form.component.html',
  styleUrl: './permission-request-form.component.scss',
  providers: [MessageService, OrgUnitsService]
})
export class PermissionRequestFormComponent implements OnInit, OnChanges {
  @Input() permissionId: string | null = null;
  @Input() isViewMode: boolean = false;
  @Input() permission: any = null;  // Add permission property
  @Input() defaultRequestType: { value: string; name?: string } | string | null = null;
  @Input() contactPrefill: {
    generalData?: { agencyOrManagement?: string };
    contactOfficer?: { email?: string; mobileNumber?: string; employeeName?: string };
    questionMaker?: { email?: string; mobileNumber?: string; employeeName?: string };
  } | null = null;
  @Input() suppressRedirect: boolean = false;
  @Input() lockRequestType: boolean = false;
  @Input() hideNativeActions: boolean = false;
  @Output() submissionStarted = new EventEmitter<void>();
  @Output() submissionSucceeded = new EventEmitter<any>();
  @Output() submissionFailed = new EventEmitter<any>();

  // Add isEditMode property
  isEditMode: boolean = false;
  private originalFormValue: any = null;

  // Add window reference
  protected window = window;

  requestForm!: FormGroup;
  isFormSubmitted = false;

  // Add permissionRequests property
  permissionRequests: any[] = [];

  treeData: any[] = [];
  selectedTreeData_orginal: any;
  selectedTreeData: any;
  selectedTreeTags_current: any;
  selectedNode: any;
  GlobalConstants = GlobalConstants;
  staticURL = GlobalConstants.staticFilesURL;

  // Dropdown options
  testGoals = [
    'Measuring overall employee performance',
    'Performance measurement for membership New employee',
    'Renewal of practicing employee',
    'Other'
  ];

  // goalOptions = ['اختبار', 'آخر']; // Test, Other

  questionCategories: { category: string; types: { type: string; name: string; icon: string }[] }[] = [];

  // Update question level options with English values and labels
  questionLevelOptions = [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Difficult' },
    { value: 'ALL', label: 'All of them' },
  ];

  testTypeOptions = ['Diverse', 'True or False', 'Multiple Choice'];
  // diverse, True or false, Multiple choice

  numberOfEmployeesOptions = [
    { label: '50 employees or less', value: '50-' },
    { label: '50 to 150 employees', value: '50-150' },
    { label: 'More than 150 employees', value: '150+' },
  ];

  questionsPerModelOptions = [20, 30, 40, 50];

  regionsOptions = [
    { label: '10 branches', value: 10 },
    { label: '20 branches', value: 20 },
    { label: '30 branches', value: 30 },
    { label: 'Other', value: 'other' }
  ];

  examinationHallsOptions = [
    { label: '10 halls', value: 10 },
    { label: '30 halls', value: 30 },
    { label: '50 halls', value: 50 },
    { label: 'Other', value: 'other' }
  ];

  // Add new property for test entries
  get testEntries() {
    return this.requestForm.get('testEntries') as FormArray;
  }

  // Update editableSections to include test management and reports
  editableSections = {
    orderData: true,  // Order Data section is always editable
    generalData: false,
    contactOfficer: false,
    questionMaker: false,
    testDetails: true,  // Make test details editable
    gradingScale: true, // Make grading scale editable
    observers: true,    // Make observers section editable
    reports: true,      // Make reports section editable
    testSchedule: true  // Make test schedule editable
  };

  constructor(
    private fb: FormBuilder, 
    private permissionService: PermissionHandlerService,
    private dataService: DataService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private confirmationService: ConfirmationService,
    private ouService: OrgUnitsService
  ) {
    // Initialize question categories from QuestionBank, excluding Thank You Messages and Impact Measurement
    this.questionCategories = QuestionBank
      .filter(category => category.category !== "Thank You Messages" && category.category !== "Impact Measurement")
      .map(category => ({
        category: category.category,
        types: category.questions.map(q => ({
          type: q.type,
          name: q.name,
          icon: q.icon
        }))
      }));
  }

  ngOnInit(): void {
    this.setupForm();
    this.setupFormSubscriptions();
    this.getTreeData();
    this.loadPermissionRequests();
    this.loadUserData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['permissionId'] && this.permissionId) {
      // If tree data is not loaded yet, wait for it before loading permission data
      if (!this.selectedTreeData || this.selectedTreeData.length === 0) {
        console.log('Permission ID changed but tree data not loaded yet, will load permission data after tree data is ready');
        // The loadPermissionData will handle waiting for tree data
      }
      this.loadPermissionData(this.permissionId);
    }
    if (changes['isViewMode']) {
      // Update form controls disabled state when view mode changes
      this.updateFormControlsDisabledState();
    }
    if (changes['defaultRequestType'] && this.requestForm && !this.isViewMode) {
      this.applyDefaultRequestType();
    }
    if (changes['contactPrefill'] && this.requestForm && !this.isViewMode) {
      this.applyContactPrefill();
    }
    if (changes['lockRequestType'] && this.requestForm && !this.isViewMode) {
      this.enforceRequestTypeLock();
    }
  }

  private setupForm(): void {
    const baseFormControls = {
      generalData: this.fb.group({
        agencyOrManagement: [{ value: '', disabled: this.isViewMode && !this.editableSections.generalData }, Validators.required],
        requestFor: [{ value: '', disabled: this.isViewMode && !this.editableSections.generalData }, Validators.required],
        ou: [{ value: '', disabled: this.isViewMode && !this.editableSections.generalData }, Validators.required]
      }),

      contactOfficer: this.fb.group({
        email: [{ value: '', disabled: this.isViewMode && !this.editableSections.contactOfficer }, [Validators.required, Validators.email]],
        mobileNumber: [{ value: '', disabled: this.isViewMode && !this.editableSections.contactOfficer }, [Validators.required, Validators.pattern(/^[0-9+\-\s()]+$/)]],
        employeeName: [{ value: '', disabled: this.isViewMode && !this.editableSections.contactOfficer }, Validators.required]
      }),

      questionMaker: this.fb.group({
        email: [{ value: '', disabled: this.isViewMode && !this.editableSections.questionMaker }, [Validators.required, Validators.email]],
        mobileNumber: [{ value: '', disabled: this.isViewMode && !this.editableSections.questionMaker }, [Validators.required, Validators.pattern(/^[0-9+\-\s()]+$/)]],
        employeeName: [{ value: '', disabled: this.isViewMode && !this.editableSections.questionMaker }, Validators.required]
      }),

      // Order Data section (always enabled for new requests)
      testGoal: [{ value: '', disabled: false }, Validators.required],
      testGoalOther: [{ value: '', disabled: false }],
      questionType: [{ value: [], disabled: false }, [Validators.required, Validators.minLength(1)]],
      questionLevel: [{ value: '', disabled: false }, Validators.required],
      numberOfEmployees: [{ value: '', disabled: false }, Validators.required],
      questionsPerModel: [{ value: '', disabled: false }, Validators.required],

      gradingScale: this.fb.group({
        notPassed: this.fb.group({
          from: [{ value: 0, disabled: false }, [Validators.required, Validators.min(0)]],
          to: [{ value: 59, disabled: false }, [Validators.required, Validators.max(100)]]
        }),
        good: this.fb.group({
          from: [{ value: 60, disabled: false }, [Validators.required, Validators.min(0)]],
          to: [{ value: 74, disabled: false }, [Validators.required, Validators.max(100)]]
        }),
        veryGood: this.fb.group({
          from: [{ value: 75, disabled: false }, [Validators.required, Validators.min(0)]],
          to: [{ value: 89, disabled: false }, [Validators.required, Validators.max(100)]]
        }),
        excellent: this.fb.group({
          from: [{ value: 90, disabled: false }, [Validators.required, Validators.min(0)]],
          to: [{ value: 100, disabled: false }, [Validators.required, Validators.max(100)]]
        })
      }),

      numberOfRegions: [{ value: '', disabled: false }, Validators.required],
      numberOfRegionsOther: [{ value: '', disabled: false }],
      examinationHalls: [{ value: '', disabled: false }, Validators.required],
      examinationHallsOther: [{ value: '', disabled: false }],
      totalObservers: [{ value: '', disabled: false }, [Validators.required, Validators.min(1)]],
      observersNote: [{ value: '', disabled: false }],

      testEntries: this.fb.array([], [Validators.required, Validators.minLength(1)]),

      detailedReport: [{ value: true, disabled: false }],
      yearlyReport: [{ value: true, disabled: false }]
    };

    // Only add comments and agreement fields in view mode
    if (this.isViewMode) {
      Object.assign(baseFormControls, {
        comments: [{ value: '', disabled: !this.editableSections.orderData }],
        agreement: [{ value: false, disabled: !this.editableSections.orderData }]
      });
    }

    this.requestForm = this.fb.group(baseFormControls);

    this.applyDefaultRequestType();
    this.applyContactPrefill();
    this.enforceRequestTypeLock();

    // Add initial test entry for new requests
    if (!this.isViewMode) {
      this.addTestEntry();
    }
  }

  private setupFormSubscriptions(): void {
    this.requestForm.get('examinationHalls')?.valueChanges.subscribe(value => {
      if (typeof value === 'number') {
        this.requestForm.patchValue({ totalObservers: value });
      }
    });

    this.requestForm.get('testGoal')?.valueChanges.subscribe(value => {
      const testGoalOtherControl = this.requestForm.get('testGoalOther');
      if (value === 'Other') {
        testGoalOtherControl?.setValidators([Validators.required]);
      } else {
        testGoalOtherControl?.clearValidators();
        testGoalOtherControl?.setValue('');
      }
      testGoalOtherControl?.updateValueAndValidity();
    });

    this.requestForm.get('numberOfRegions')?.valueChanges.subscribe(value => {
      const control = this.requestForm.get('numberOfRegionsOther');
      if (value === 'other') {
        control?.setValidators([Validators.required]);
      } else {
        control?.clearValidators();
        control?.setValue('');
      }
      control?.updateValueAndValidity();
    });

    this.requestForm.get('examinationHalls')?.valueChanges.subscribe(value => {
      const control = this.requestForm.get('examinationHallsOther');
      if (value === 'other') {
        control?.setValidators([Validators.required]);
      } else {
        control?.clearValidators();
        control?.setValue('');
      }
      control?.updateValueAndValidity();
    });

    this.requestForm.get('numberOfEmployees')?.valueChanges.subscribe(value => {
      this.adjustTestEntriesBasedOnEmployees(value);
    });

    // Subscribe to request type changes to update test entries
    this.requestForm.get('generalData.requestFor')?.valueChanges.subscribe(value => {
      this.updateTestEntriesForRequestType(value);
    });
  }

  // Method to update test entries when request type changes
  private updateTestEntriesForRequestType(requestType: any): void {
    if (!this.testEntries || this.testEntries.length === 0) return;

    const isAssessment = requestType && (requestType.value === 'ASSESSMENT' || requestType === 'ASSESSMENT');
    
    this.testEntries.controls.forEach((entry, index) => {
      const currentEntry = entry as FormGroup;
      
      if (isAssessment) {
        // Add attemptTime field if it doesn't exist
        if (!currentEntry.get('attemptTime')) {
          currentEntry.addControl('attemptTime', this.fb.control(null, [Validators.required, Validators.min(1)]));
        }
      } else {
        // Remove attemptTime field if it exists
        if (currentEntry.get('attemptTime')) {
          currentEntry.removeControl('attemptTime');
        }
      }
    });
  }

  // Add helper method to check if the request is for ASSESSMENT type
  isAssessmentRequest(): boolean {
    const requestType = this.requestForm.get('generalData.requestFor')?.value;
    return requestType && (requestType.value === 'ASSESSMENT' || requestType === 'ASSESSMENT');
  }

  // Method to adjust test entries based on number of employees
  private adjustTestEntriesBasedOnEmployees(employeeCount: string): void {
    if (!employeeCount) return;

    const currentEntriesCount = this.testEntries.length;
    let requiredEntriesCount = 0;

    switch (employeeCount) {
      case '50-':
        requiredEntriesCount = 1;
        break;
      case '50-150':
        requiredEntriesCount = 2;
        break;
      case '150+':
        requiredEntriesCount = 3;
        break;
      default:
        return;
    }

    // If we need more entries, add them
    while (this.testEntries.length < requiredEntriesCount) {
      this.addTestEntry();
    }

    // If we have too many entries, remove the excess ones (from the end)
    while (this.testEntries.length > requiredEntriesCount) {
      this.testEntries.removeAt(this.testEntries.length - 1);
    }
  }

  // Helper method to get maximum allowed test entries based on employee count
  getMaxTestEntries(): number {
    const employeeCount = this.requestForm.get('numberOfEmployees')?.value;
    
    switch (employeeCount) {
      case '50-':
        return 1;
      case '50-150':
        return 2;
      case '150+':
        return 3;
      default:
        return 1; // Default to 1 if no selection
    }
  }

  // Method to check if "Add Test Session" button should be shown
  canAddTestEntry(): boolean {
    const currentEntriesCount = this.testEntries.length;
    const maxEntries = this.getMaxTestEntries();
    return currentEntriesCount < maxEntries;
  }

  // Method to check if remove button should be shown for a specific entry
  canRemoveTestEntry(index: number): boolean {
    const currentEntriesCount = this.testEntries.length;
    const maxEntries = this.getMaxTestEntries();
    
    // Allow removal if we have more entries than the minimum required
    return currentEntriesCount > maxEntries;
  }

  // Update createTestEntry method to ensure it's enabled for new requests
  createTestEntry(): FormGroup {
    const baseControls = {
      testName: [{ value: '', disabled: false }, [Validators.required, Validators.minLength(3)]],
      testDate: [{ value: '', disabled: false }, Validators.required],
      startTime: [{ value: '', disabled: false }, Validators.required],
      endTime: [{ value: '', disabled: false }, Validators.required]
    };

    // Add attemptTime field for ASSESSMENT type requests
    const requestType = this.requestForm.get('generalData.requestFor')?.value;
    if (requestType && (requestType.value === 'ASSESSMENT' || requestType === 'ASSESSMENT')) {
      Object.assign(baseControls, {
        attemptTime: [{ value: null, disabled: false }, [Validators.required, Validators.min(1)]]
      });
    }

    return this.fb.group(baseControls);
  }

  // Add method to add a new test entry
  addTestEntry(): void {
    // Check if we can add more entries based on employee count
    if (!this.canAddTestEntry()) {
      return;
    }
    this.testEntries.push(this.createTestEntry());
  }

  // Add method to remove a test entry
  removeTestEntry(index: number): void {
    // Check if we can remove this entry based on employee count
    if (!this.canRemoveTestEntry(index)) {
      return;
    }
    
    if (confirm('Are you sure you want to remove this test session?')) {
      this.testEntries.removeAt(index);
      // Revalidate the form after removal
      this.validateTestEntries();
    }
  }

  // Add method to get test entries array
  getTestEntries(): FormArray {
    return this.requestForm.get('testEntries') as FormArray;
  }

  async onSubmit(): Promise<void> {
    const validation = this.validateAndGetPayload();
    if (!validation.valid || !validation.payload) {
      return;
    }
    await this.sendFormData(validation.payload);
  }

  public validateAndGetPayload(): { valid: boolean; payload?: any } {
    const isValid = this.runFormValidations();
    if (!isValid) {
      this.scrollToFirstError();
      return { valid: false };
    }
    const payload = this.buildSubmissionPayload();
    return { valid: true, payload };
  }

  private runFormValidations(): boolean {
    this.isFormSubmitted = true;
    this.markAllControlsAsTouched(this.requestForm);

    if (this.isViewMode) {
      const enabledControls = this.getEnabledControls(this.requestForm);
      const controlsToValidate = enabledControls.filter(control => control !== this.requestForm.get('agreement'));
      return controlsToValidate.every(control => control.valid);
    }

    this.validateTestEntries();
    this.enforceRequiredSelections();
    this.validateGradingScale();

    return this.requestForm.valid;
  }

  private enforceRequiredSelections(): void {
    const questionTypes = this.requestForm.get('questionType')?.value || [];
    if (questionTypes.length === 0) {
      this.requestForm.get('questionType')?.setErrors({ 'required': true });
    }

    const testGoal = this.requestForm.get('testGoal')?.value;
    if (!testGoal) {
      this.requestForm.get('testGoal')?.setErrors({ 'required': true });
    }

    const questionLevel = this.requestForm.get('questionLevel')?.value;
    if (!questionLevel) {
      this.requestForm.get('questionLevel')?.setErrors({ 'required': true });
    }

    const numberOfEmployees = this.requestForm.get('numberOfEmployees')?.value;
    if (!numberOfEmployees) {
      this.requestForm.get('numberOfEmployees')?.setErrors({ 'required': true });
    }

    const questionsPerModel = this.requestForm.get('questionsPerModel')?.value;
    if (!questionsPerModel) {
      this.requestForm.get('questionsPerModel')?.setErrors({ 'required': true });
    }

    const numberOfRegions = this.requestForm.get('numberOfRegions')?.value;
    if (!numberOfRegions) {
      this.requestForm.get('numberOfRegions')?.setErrors({ 'required': true });
    }

    const examinationHalls = this.requestForm.get('examinationHalls')?.value;
    if (!examinationHalls) {
      this.requestForm.get('examinationHalls')?.setErrors({ 'required': true });
    }

    if (testGoal === 'Other') {
      const otherValue = this.requestForm.get('testGoalOther')?.value;
      if (!otherValue) {
        this.requestForm.get('testGoalOther')?.setErrors({ 'required': true });
      }
    }

    if (numberOfRegions === 'other') {
      const otherValue = this.requestForm.get('numberOfRegionsOther')?.value;
      if (!otherValue) {
        this.requestForm.get('numberOfRegionsOther')?.setErrors({ 'required': true });
      }
    }

    if (examinationHalls === 'other') {
      const otherValue = this.requestForm.get('examinationHallsOther')?.value;
      if (!otherValue) {
        this.requestForm.get('examinationHallsOther')?.setErrors({ 'required': true });
      }
    }
  }

  private validateGradingScale(): void {
    const gradingScale = this.requestForm.get('gradingScale');
    if (!gradingScale) return;

    const notPassed = gradingScale.get('notPassed');
    const good = gradingScale.get('good');
    const veryGood = gradingScale.get('veryGood');
    const excellent = gradingScale.get('excellent');

    // Validate ranges are sequential
    if (notPassed && good && veryGood && excellent) {
      const notPassedTo = notPassed.get('to')?.value;
      const goodFrom = good.get('from')?.value;
      const goodTo = good.get('to')?.value;
      const veryGoodFrom = veryGood.get('from')?.value;
      const veryGoodTo = veryGood.get('to')?.value;
      const excellentFrom = excellent.get('from')?.value;

      if (notPassedTo >= goodFrom) {
        good.get('from')?.setErrors({ 'invalidRange': true });
      }
      if (goodTo >= veryGoodFrom) {
        veryGood.get('from')?.setErrors({ 'invalidRange': true });
      }
      if (veryGoodTo >= excellentFrom) {
        excellent.get('from')?.setErrors({ 'invalidRange': true });
      }
    }
  }

  private markAllControlsAsTouched(formGroup: FormGroup | FormArray): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markAllControlsAsTouched(control);
      } else {
        control?.markAsTouched();
        control?.updateValueAndValidity({ emitEvent: true });
      }
    });
  }

  private validateTestEntries(): void {
    if (this.testEntries.length === 0) {
      this.requestForm.get('testEntries')?.setErrors({ 'required': true });
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time part to compare dates only

    this.testEntries.controls.forEach((entry, index) => {
      const startTime = entry.get('startTime')?.value;
      const endTime = entry.get('endTime')?.value;
      const testDate = entry.get('testDate')?.value;
      
      // Validate test date
      if (testDate) {
        const selectedDate = new Date(testDate);
        selectedDate.setHours(0, 0, 0, 0);
        
        if (selectedDate < today) {
          entry.get('testDate')?.setErrors({ 'pastDate': true });
        }
      }
      
      // Validate time range
      if (startTime && endTime) {
        const start = new Date(`2000-01-01T${startTime}`);
        const end = new Date(`2000-01-01T${endTime}`);
        
        if (end <= start) {
          entry.get('endTime')?.setErrors({ 'invalidTime': true });
        }
      }
    });
  }

  private scrollToFirstError(): void {
    const firstError = document.querySelector('.is-invalid');
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.requestForm.get(fieldName);
    if (!field) return false;
    
    // Always show errors if form is submitted
    if (this.isFormSubmitted) {
      return field.invalid;
    }
    
    // Otherwise only show if field is touched/dirty
    return field.invalid && (field.dirty || field.touched);
  }

  isFormGroupInvalid(groupName: string): boolean {
    const group = this.requestForm.get(groupName);
    if (!group) return false;
    
    // Always show errors if form is submitted
    if (this.isFormSubmitted) {
      return group.invalid;
    }
    
    // Otherwise only show if group is touched/dirty
    return group.invalid && (group.dirty || group.touched);
  }

  isTestEntryFieldInvalid(index: number, field: string): boolean {
    const entry = this.testEntries.at(index);
    if (!entry) return false;
    
    const control = entry.get(field);
    if (!control) return false;
    
    // Always show errors if form is submitted
    if (this.isFormSubmitted) {
      return control.invalid;
    }
    
    // Otherwise only show if field is touched/dirty
    return control.invalid && (control.dirty || control.touched);
  }

  getFieldError(fieldName: string): string {
    const field = this.requestForm.get(fieldName);
    if (!field?.errors) return '';

    const errors = field.errors;
    const label = this.getFieldLabel(fieldName);

    if (errors['required']) {
      return `${label} is required`;
    }
    if (errors['email']) {
      return 'Email is invalid';
    }
    if (errors['pattern']) {
      return 'Please enter a valid format';
    }
    if (errors['min']) {
      return `${label} must be at least ${errors['min'].min}`;
    }
    if (errors['max']) {
      return `${label} must be at most ${errors['max'].max}`;
    }
    if (errors['minlength']) {
      return `${label} must be at least ${errors['minlength'].requiredLength} characters`;
    }
    if (errors['invalidTime']) {
      return 'The end time cannot be before the start time';
    }
    if (errors['pastDate']) {
      return 'Test date cannot be in the past';
    }
    if (errors['invalidRange']) {
      return `${label} range must be sequential with other ranges`;
    }

    return `${label} is invalid`;
  }

  private getFieldLabel(fieldName: string): string {
    const fieldLabels: { [key: string]: string } = {
      'generalData.agencyOrManagement': 'Agency or Management',
      'generalData.requestFor': 'Request Type',
      'generalData.ou': 'Organizational Unit',
      'contactOfficer.email': 'Email',
      'contactOfficer.mobileNumber': 'Mobile Number',
      'contactOfficer.employeeName': 'Employee Name',
      'questionMaker.email': 'Email',
      'questionMaker.mobileNumber': 'Mobile Number',
      'questionMaker.employeeName': 'Employee Name',
      'testGoal': 'Test Goal',
      'testGoalOther': 'Test Goal (Other)',
      'questionType': 'Question Type',
      'questionLevel': 'Question Level',
      'numberOfEmployees': 'Number of Employees',
      'questionsPerModel': 'Number of Questions per Model',
      'numberOfRegions': 'Number of Regions',
      'numberOfRegionsOther': 'Number of Regions (Other)',
      'examinationHalls': 'Examination Halls',
      'examinationHallsOther': 'Examination Halls (Other)',
      'testEntries': 'Test Sessions',
      'attemptTime': 'Attempt Time (minutes)'
    };

    return fieldLabels[fieldName] || fieldName;
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    this.isFormSubmitted = true;
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control as FormGroup);
      } else {
        control?.markAsTouched({ onlySelf: true });
      }
    });
  }

  hasPermission(key:string, flag:string) {
    return this.permissionService.hasPermission(key, flag);
  }

  async getTreeData() {
    try {
      console.log('Loading tree data...');
      this.treeData = await this.ouService.getOrgUnits();
      console.log('Tree data loaded:', this.treeData);
      
      if (!this.treeData || !Array.isArray(this.treeData)) {
        console.error('Invalid tree data received:', this.treeData);
        this.treeData = [];
      }
      
      this.selectedTreeData_orginal = cloneDeep(this.treeData);
      console.log('Original tree data cloned:', this.selectedTreeData_orginal);
      
      await this.changeSelectedTag(null);
      console.log("Final selected tree data:", this.selectedTreeData);
    } catch (error) {
      console.error('Error loading tree data:', error);
      this.treeData = [];
      this.selectedTreeData_orginal = [];
      this.selectedTreeData = [];
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Failed to load organizational units data')
      });
    }
  }

  findNodeByName(data: any[], nodeName: any): any | undefined {
    for (const node of data) {
      if (node.children) {
        const result = this.findNodeByName(node.children, nodeName);
        if (result) {
          return result;
        }
      }
    }
    return undefined;
  }

  async changeSelectedTag(event: any) {
    try {
      console.log("change tag event", event);
      this.selectedTreeTags_current = {
        "label": "Ministries/Entities"
      };
      if (this.selectedTreeTags_current.label) {
        console.log("change tag event1", event, this.selectedTreeData);
        await this.filterTreeByTag("Ministries/Entities");
      } else {
        this.selectedTreeData = null;
      }
    } catch (error) {
      console.error('Error in changeSelectedTag:', error);
      this.selectedTreeData = this.selectedTreeData_orginal || [];
    }
  }

  filterTreeByTag(tag: string) {
    try {
      if (!this.selectedTreeData_orginal || !Array.isArray(this.selectedTreeData_orginal)) {
        console.warn('No original tree data available for filtering');
        this.selectedTreeData = [];
        return;
      }

      this.selectedTreeData = cloneDeep(this.selectedTreeData_orginal);
      this.selectedTreeData = this.selectedTreeData
        .map((node: any) => this.filterNodes(node, tag))
        .filter((filteredNode: any) => filteredNode !== null);
      console.log('Filtered tree data:', this.selectedTreeData);

      if (this.selectedTreeData.length == 0) {
        console.log('No nodes found with tag, creating fallback structure');
        let cloneitems: any = []
        this.selectedTreeData_orginal.forEach((item: any) => {
          cloneitems.push({
            data: item.data,
            id: item.id,
            label: item.label,
            parent: item.parent,
            tag: item.tag,
            _id: item._id,
            children: []
          })
        });

        console.log("cloneitems.children", cloneitems);
        this.selectedTreeData = cloneDeep(cloneitems);
        console.log("cloneitems.children this.selectedTreeData", this.selectedTreeData);
      }
    } catch (error) {
      console.error('Error in filterTreeByTag:', error);
      this.selectedTreeData = this.selectedTreeData_orginal || [];
    }
  }

  filterNodes(node: any, tag: string): any | null {
    try {
      if (!node) {
        return null;
      }

      if (node.children && Array.isArray(node.children) && node.children.length > 0) {
        node.children = node.children
          .map((childNode: any) => this.filterNodes(childNode, tag))
          .filter((filteredChild: any) => filteredChild !== null);
      }

      if (node.tag === tag || (node.children && Array.isArray(node.children) && node.children.length > 0)) {
        return node;
      }
      return null;
    } catch (error) {
      console.error('Error in filterNodes:', error, { node, tag });
      return null;
    }
  }
  checkisImg(arg0: string | undefined): any {
    if (arg0) {
      return /\.(jpg|jpeg|png|gif|svg)/.test(arg0);
    } else {
      return false;
    }
  }

  private loadPermissionRequests(): void {
    const storedRequests = localStorage.getItem('permissionRequests');
    if (storedRequests) {
      try {
        const parsedRequests = JSON.parse(storedRequests);
        // Transform the data to include name and value keys
        this.permissionRequests = parsedRequests.map((item: any, index: number) => ({
          name: item, // Use existing name or generate one
          value: item, // Use existing value or generate one
        }));

        // Set default selection if there are requests
        if (this.permissionRequests.length > 0) {
          const defaultRequest = this.permissionRequests[0];
          this.requestForm.get('generalData.requestFor')?.setValue(defaultRequest);
          console.log('Default request set:', defaultRequest);
        } else {
          if(this.hasPermission('PERMISSION_REQUESTS','r')){
            this.permissionRequests = [
              {
                value : 'SURVEY',
                name : this.translate('SURVEY')
              },
              {
                value : 'ASSESSMENT',
                name: this.translate('ASSESSMENT')
              }
            ] 
          }
        }
      } catch (error) {
        console.error('Error parsing permission requests from localStorage:', error);
        this.permissionRequests = [];
      }
    } else {
      console.log('No permission requests found in localStorage');
    }

    if (this.permissionRequests.length === 0 && this.defaultRequestType) {
      const option = this.normalizeRequestType(this.defaultRequestType);
      if (option) {
        this.permissionRequests = [option];
      }
    }
  }

  onRequestTypeChange(event: any): void {
    if (event.value) {
      console.log('Selected request type:', {
        name: event.value.name,
        value: event.value.value,
      });
    }
  }

  private loadUserData(): void {
    // Get user data from localStorage
    const username = localStorage.getItem('username');
    const email = localStorage.getItem('email');
    const phone = localStorage.getItem('phone');

    // Fill the contact officer form group with user data
    if (username || email || phone) {
      this.requestForm.patchValue({
        contactOfficer: {
          employeeName: username || '',
          email: email || '',
          mobileNumber: phone || ''
        }
      });
      console.log('User data loaded:', { username, email, phone });
    } else {
      console.log('No user data found in localStorage');
    }
  }

  // Update the method to handle checkbox changes
  onQuestionTypeChange(event: Event, type: string): void {
    const checkbox = event.target as HTMLInputElement;
    const currentTypes = this.requestForm.get('questionType')?.value || [];
    
    if (checkbox.checked) {
      this.requestForm.patchValue({
        questionType: [...currentTypes, type]
      });
    } else {
      this.requestForm.patchValue({
        questionType: currentTypes.filter((t: string) => t !== type)
      });
    }
  }

  // Add a method to check if a question type is selected
  isQuestionTypeSelected(type: string): boolean {
    const selectedTypes = this.requestForm.get('questionType')?.value || [];
    return selectedTypes.includes(type);
  }

  // Add method to get form group errors
  getFormGroupErrors(groupName: string): string[] {
    const group = this.requestForm.get(groupName);
    if (!group || !group.errors) return [];

    return Object.keys(group.errors).map(key => {
      switch (key) {
        case 'required':
          return 'This section is required';
        case 'minlength':
          return `Minimum length is ${group.errors?.[key].requiredLength} characters`;
        default:
          return 'Invalid value';
      }
    });
  }

  // Update resetForm method to properly reset all form fields
  resetForm(): void {
    this.isFormSubmitted = false;
    this.isEditMode = false;
    
    // Reset the form with default values
    this.requestForm.reset({
      generalData: {
        agencyOrManagement: '',
        requestFor: '',
        ou: []
      },
      contactOfficer: {
        email: '',
        mobileNumber: '',
        employeeName: ''
      },
      questionMaker: {
        email: '',
        mobileNumber: '',
        employeeName: ''
      },
      testGoal: '',
      testGoalOther: '',
      questionType: [],
      questionLevel: '',
      numberOfEmployees: '',
      questionsPerModel: '',
      gradingScale: {
        notPassed: { from: 0, to: 59 },
        good: { from: 60, to: 74 },
        veryGood: { from: 75, to: 89 },
        excellent: { from: 90, to: 100 }
      },
      numberOfRegions: '',
      numberOfRegionsOther: '',
      examinationHalls: '',
      examinationHallsOther: '',
      totalObservers: '',
      observersNote: '',
      detailedReport: true,
      yearlyReport: true,
      comments: '',
      agreement: false  // Ensure agreement is unchecked by default
    });

    // Reset test entries
    while (this.testEntries.length) {
      this.testEntries.removeAt(0);
    }
    this.addTestEntry();  // Add one empty test entry

    // Reset validation state
    this.requestForm.markAsUntouched();
    this.requestForm.markAsPristine();
    
    // Update form controls disabled state
    this.updateFormControlsDisabledState();
    
    // Load user data again for contact officer
    this.loadUserData();
  }

  getInvalidControls(): string[] {
    const invalidControls: string[] = [];
    
    const addInvalidControl = (control: AbstractControl, path: string = '') => {
      if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach(key => {
          const childControl = control.get(key);
          if (childControl) {
            const childPath = path ? `${path}.${key}` : key;
            addInvalidControl(childControl, childPath);
          }
        });
      } else if (control instanceof FormArray) {
        control.controls.forEach((childControl, index) => {
          const childPath = `${path}[${index}]`;
          addInvalidControl(childControl, childPath);
        });
      } else {
        if (control.invalid && (this.isFormSubmitted || control.dirty || control.touched)) {
          invalidControls.push(path);
        }
      }
    };

    addInvalidControl(this.requestForm);
    return invalidControls;
  }

  // Add method to get minimum date for date input
  getMinDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  translate(key: string) {
    let translator: TranslatorPipe = new TranslatorPipe();
    return translator.transform(key);
  }

  private async loadPermissionData(permissionId: string) {
    console.log('Starting to load permission data for ID:', permissionId);
    console.log('Current tree data:', this.selectedTreeData);
    
    try {
      console.log('Making API call to fetch permission data...');
      const response = await this.dataService.getData(`permission-request/${permissionId}`);
      console.log('API Response:', response);
      
      if (!response.error && response.data) {
        this.permission = response.data;
        const permissionData = response.data;

        // Store current form state
        const currentComments = this.requestForm.get('comments')?.value;
        const currentAgreement = this.requestForm.get('agreement')?.value;
        const wasEditMode = this.isEditMode;

        // Reset the form first
        this.resetForm();

        // Wait for tree data to be loaded if it's not available yet
        if (!this.selectedTreeData || this.selectedTreeData.length === 0) {
          console.log('Tree data not loaded yet, waiting for it...');
          try {
            await this.waitForTreeData();
          } catch (error) {
            console.error('Timeout waiting for tree data, proceeding without OU data:', error);
            // Continue without OU data if timeout occurs
          }
        }

        // Transform OU data to match the tree structure
        console.log('Transforming OU data...');
        console.log('OU IDs from permission data:', permissionData.generalData.ou);
        
        let ouData: any[] = [];
        if (permissionData.generalData.ou && Array.isArray(permissionData.generalData.ou)) {
          if (this.selectedTreeData && this.selectedTreeData.length > 0) {
            ouData = permissionData.generalData.ou.map((ouId: string) => {
              console.log('Looking for OU ID:', ouId);
              const node = this.findNodeById(this.selectedTreeData, ouId);
              console.log('Found node:', node);
              return node;
            }).filter(Boolean);
          } else {
            console.warn('Tree data still not available, creating fallback OU data');
            // Create fallback OU data with just the IDs
            ouData = permissionData.generalData.ou.map((ouId: string) => ({
              _id: ouId,
              label: `OU ${ouId}`,
              data: { name: `OU ${ouId}` }
            }));
          }
        }
        
        console.log('Transformed OU data:', ouData);

        // Patch the form with the loaded data
        console.log('Patching form with data...');
        const formData = {
          generalData: {
            agencyOrManagement: permissionData.generalData?.agencyOrManagement || '',
            requestFor: permissionData.generalData?.requestFor || '',
            ou: ouData
          },
          contactOfficer: {
            email: permissionData.contactOfficer?.email || '',
            mobileNumber: permissionData.contactOfficer?.mobileNumber || '',
            employeeName: permissionData.contactOfficer?.employeeName || ''
          },
          questionMaker: {
            email: permissionData.questionMaker?.email || '',
            mobileNumber: permissionData.questionMaker?.mobileNumber || '',
            employeeName: permissionData.questionMaker?.employeeName || ''
          },
          testGoal: permissionData.testGoal || '',
          testGoalOther: permissionData.testGoalOther || '',
          questionType: permissionData.questionType || [],
          questionLevel: permissionData.questionLevel || '',
          numberOfEmployees: permissionData.numberOfEmployees || '',
          questionsPerModel: permissionData.questionsPerModel || '',
          gradingScale: permissionData.gradingScale || {
            notPassed: { from: 0, to: 59 },
            good: { from: 60, to: 74 },
            veryGood: { from: 75, to: 89 },
            excellent: { from: 90, to: 100 }
          },
          numberOfRegions: permissionData.numberOfRegions || '',
          numberOfRegionsOther: permissionData.numberOfRegionsOther || '',
          examinationHalls: permissionData.examinationHalls || '',
          examinationHallsOther: permissionData.examinationHallsOther || '',
          totalObservers: permissionData.totalObservers || '',
          observersNote: permissionData.observersNote || '',
          detailedReport: permissionData.detailedReport !== undefined ? permissionData.detailedReport : true,
          yearlyReport: permissionData.yearlyReport !== undefined ? permissionData.yearlyReport : true,
          comments: currentComments || permissionData.comments || '',
          agreement: currentAgreement !== undefined ? currentAgreement : (permissionData.agreement || false)
        };
        
        console.log('Form data to be patched:', formData);
        this.requestForm.patchValue(formData);
        console.log('Form after patch:', this.requestForm.value);

        // Handle test entries separately since it's a FormArray
        console.log('Handling test entries...');
        while (this.testEntries.length) {
          this.testEntries.removeAt(0);
        }
        
        console.log('Test entries from permission data:', permissionData.testEntries);
        if (Array.isArray(permissionData.testEntries)) {
          permissionData.testEntries.forEach((entry: any) => {
            console.log('Adding test entry:', entry);
            
            const baseControls = {
              testName: [entry.testName || '', [Validators.required, Validators.minLength(3)]],
              testDate: [entry.testDate || '', Validators.required],
              startTime: [entry.startTime || '', Validators.required],
              endTime: [entry.endTime || '', Validators.required]
            };

            // Add attemptTime field for ASSESSMENT type requests
            const requestType = permissionData.generalData?.requestFor;
            if (requestType && (requestType.value === 'ASSESSMENT' || requestType === 'ASSESSMENT')) {
              Object.assign(baseControls, {
                attemptTime: [entry.attemptTime || null, [Validators.required, Validators.min(1)]]
              });
            }

            this.testEntries.push(this.fb.group(baseControls));
          });
        }

        // Restore edit mode if it was enabled
        if (wasEditMode) {
          this.isEditMode = true;
          this.updateFormControlsDisabledState();
        }

        // Mark form as pristine since we're loading existing data
        this.requestForm.markAsPristine();
        this.requestForm.markAsUntouched();
        console.log('Form loading completed successfully');

        // Update form controls disabled state
        this.updateFormControlsDisabledState();
      } else {
        console.error('API response error or no data:', response);
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate('Failed to load permission data')
        });
      }
    } catch (error) {
      console.error('Error loading permission data:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('An error occurred while loading the permission data')
      });
    }
  }

  // Helper method to wait for tree data to be loaded
  private async waitForTreeData(): Promise<void> {
    return new Promise((resolve, reject) => {
      const maxWaitTime = 10000; // 10 seconds timeout
      const startTime = Date.now();
      
      const checkTreeData = () => {
        if (this.selectedTreeData && this.selectedTreeData.length > 0) {
          console.log('Tree data loaded successfully');
          resolve();
        } else if (Date.now() - startTime > maxWaitTime) {
          console.error('Timeout waiting for tree data');
          reject(new Error('Timeout waiting for tree data'));
        } else {
          setTimeout(checkTreeData, 100); // Check again in 100ms
        }
      };
      checkTreeData();
    });
  }

  // Helper method to find a node by ID in the tree
  private findNodeById(nodes: any[], id: string): any {
    if (!nodes || !Array.isArray(nodes) || !id) {
      console.warn('Invalid parameters for findNodeById:', { nodes, id });
      return null;
    }

    for (const node of nodes) {
      if (node && node._id === id) {
        return node;
      }
      if (node && node.children && Array.isArray(node.children)) {
        const found = this.findNodeById(node.children, id);
        if (found) return found;
      }
    }
    console.warn('Node not found for ID:', id);
    return null;
  }

  // Update method to handle form controls disabled state
  private updateFormControlsDisabledState(): void {
    if (!this.requestForm) return;

    // For new requests, don't apply any disabled states
    if (!this.isViewMode) {
      return;
    }

    const controls = this.requestForm.controls;
    Object.keys(controls).forEach(key => {
      const control = controls[key];
      if (control instanceof FormGroup) {
        this.updateFormGroupDisabledState(control, key);
      } else if (control instanceof FormArray) {
        this.updateFormArrayDisabledState(control);
      } else {
        // Handle comments and agreement controls based on permission status
        if (key === 'comments' || key === 'agreement') {
          this.updateCommentsAgreementDisabledState(control, key);
        } else {
          this.updateControlDisabledState(control, key);
        }
      }
    });
  }

  private updateFormGroupDisabledState(group: FormGroup, groupName: string): void {
    Object.keys(group.controls).forEach(key => {
      const control = group.get(key);
      if (control instanceof FormGroup) {
        this.updateFormGroupDisabledState(control, `${groupName}.${key}`);
      } else if (control instanceof FormArray) {
        this.updateFormArrayDisabledState(control);
      } else {
        this.updateControlDisabledState(control, `${groupName}.${key}`);
      }
    });
  }

  private updateFormArrayDisabledState(array: FormArray): void {
    array.controls.forEach((control, index) => {
      if (control instanceof FormGroup) {
        this.updateFormGroupDisabledState(control, `testEntries[${index}]`);
      } else if (control instanceof FormArray) {
        this.updateFormArrayDisabledState(control);
      } else {
        this.updateControlDisabledState(control, `testEntries[${index}]`);
      }
    });
  }

  private updateControlDisabledState(control: AbstractControl | null, key: string): void {
    if (!control || !this.isViewMode || key === 'comments' || key === 'agreement') return;

    // Get the section name from the key
    const sectionName = key.split('.')[0];
    
    // Always disabled sections
    const alwaysDisabledSections = ['generalData', 'contactOfficer', 'questionMaker'];
    
    // If we're not in edit mode, disable everything except comments and agreement
    if (!this.isEditMode) {
      control.disable();
      return;
    }
    
    // If we are in edit mode
    if (this.isEditMode) {
      // If the section is in alwaysDisabledSections, keep it disabled
      if (alwaysDisabledSections.includes(sectionName)) {
        control.disable();
      } else {
        // Enable all other sections
        control.enable();
      }
    }
  }

  // New method to handle comments and agreement disabled state
  private updateCommentsAgreementDisabledState(control: AbstractControl | null, key: string): void {
    if (!control || !this.isViewMode) return;

    // If permission is approved or rejected, disable comments and agreement
    if (this.permission?.status === 'APPROVED' || this.permission?.status === 'REJECTED') {
      control.disable();
    } else if (this.permission?.status === 'PENDING') {
      // If status is pending, enable comments and agreement
      control.enable();
    } else {
      // For other cases, keep the current state
      // This handles cases where permission status might not be set yet
    }
  }

  private buildSubmissionPayload() {
    const formData = this.requestForm.getRawValue();

    if (this.isViewMode) {
      delete formData.agreement;
      delete formData.comments;
    }

    if (formData.generalData?.ou) {
      formData.generalData.ou = formData.generalData.ou
        .filter((ou: any) => ou != null && ou._id != null)
        .map((ou: any) => ou._id)
        .filter((id: any) => id != null && typeof id === 'string');
    }

    if (formData.numberOfRegions === 'other' && formData.numberOfRegionsOther) {
      formData.numberOfRegions = Number(formData.numberOfRegionsOther);
      delete formData.numberOfRegionsOther;
    } else if (formData.numberOfRegions && formData.numberOfRegions !== 'other') {
      formData.numberOfRegions = Number(formData.numberOfRegions);
    }

    if (formData.examinationHalls === 'other' && formData.examinationHallsOther) {
      formData.examinationHalls = Number(formData.examinationHallsOther);
      delete formData.examinationHallsOther;
    } else if (formData.examinationHalls && formData.examinationHalls !== 'other') {
      formData.examinationHalls = Number(formData.examinationHalls);
    }

    if (formData.totalObservers) {
      formData.totalObservers = Number(formData.totalObservers);
    }

    if (formData.questionsPerModel) {
      formData.questionsPerModel = Number(formData.questionsPerModel);
    }

    return formData;
  }

  private async sendFormData(formData: any): Promise<void> {
    try {
      const endpoint = 'permission-request/';
      const method = this.isViewMode ? 'PUT' : 'POST';

      if (method === 'PUT') formData._id = this.permissionId;

      console.log('Submitting form data:', formData);

      this.submissionStarted.emit();
      const response = await this.dataService.postData(endpoint, formData, method);
      
      if (!response.error) {
        // Disable edit mode and update form state
        this.isEditMode = false;
        
        // Update the permission status locally
        if (this.permission) {
          this.permission.status = this.isViewMode ? 'APPROVED' : 'REJECTED';
        }

        // Update form controls disabled state to reflect new status
        this.updateFormControlsDisabledState();

        this.messageService.add({
          severity: 'success',
          summary: this.translate('Success'),
          detail: this.translate(this.isViewMode ? 'Permission request has been updated successfully' : 'Permission request has been submitted successfully'),
          life: 3000
        });

        this.submissionSucceeded.emit(response);

        if (!this.isViewMode) {
          if (!this.suppressRedirect) {
            window.location.href = '/permission/requests';
          }
        } else {
          // For updates, reload the data and disable edit mode
          if (this.permissionId) {
            // Store current comments and agreement
            const currentComments = this.requestForm.get('comments')?.value;
            const currentAgreement = this.requestForm.get('agreement')?.value;

            // Disable edit mode
            this.isEditMode = false;
            
            // Reload the permission data
            await this.loadPermissionData(this.permissionId);
            
            // Restore comments and agreement
            if (currentComments) {
              this.requestForm.patchValue({ comments: currentComments });
            }
            if (currentAgreement !== undefined) {
              this.requestForm.patchValue({ agreement: currentAgreement });
            }

            // Explicitly enable comments and agreement controls if status is PENDING
            if (this.permission?.status === 'PENDING') {
              this.requestForm.get('comments')?.enable();
              this.requestForm.get('agreement')?.enable();
            }

            // Update form controls disabled state
            this.updateFormControlsDisabledState();
          }
        }
      } else {
        // Add more detailed error message
        const errorMessage = response.message || 'Failed to ' + (this.isViewMode ? 'update' : 'submit') + ' permission request';
        this.submissionFailed.emit(errorMessage);
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate(errorMessage)
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      this.submissionFailed.emit(error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('An error occurred while submitting the form')
      });
    }
  }

  // Update toggleEditMode method
  toggleEditMode(): void {
    // Don't allow editing if the permission is approved or rejected
    if (this.permission?.status !== 'PENDING') {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate('Warning'),
        detail: this.translate('Cannot edit an approved or rejected request')
      });
      return;
    }

    if (!this.isEditMode) {
      // Store original form value before enabling edit mode
      this.originalFormValue = cloneDeep(this.requestForm.getRawValue());
    }
    this.isEditMode = !this.isEditMode;
    this.updateFormControlsDisabledState();
  }

  // Update handlePermissionAction to require agreement for approve/reject
  async handlePermissionAction(permissionId: string | null, action: 'approve' | 'reject'): Promise<void> {
    // Don't proceed if permissionId is null
    if (!permissionId) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Invalid permission request ID')
      });
      return;
    }

    // Don't allow approve/reject if already approved or rejected
    if (this.permission?.status !== 'PENDING') {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate('Warning'),
        detail: this.translate('This request has already been ' + this.permission?.status.toLowerCase())
      });
      return;
    }

    // Get the current values of agreement and comments
    let agreement = this.requestForm.get('agreement')?.value;
    const comments = this.requestForm.get('comments')?.value;

    // Validate required fields for both approve and reject
    if (!agreement) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Please agree to the terms and conditions')
      });
      return;
    }

    if (!comments) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Please add comments before ' + (action === 'approve' ? 'approving' : 'rejecting'))
      });
      return;
    }

    // Show confirmation dialog
    this.confirmationService.confirm({
      message: this.translate(`Are you sure you want to ${action} this permission request?`),
      header: this.translate(`${action === 'approve' ? 'Approve' : 'Reject'} Confirmation`),
      icon: action === 'approve' ? 'pi pi-check-circle' : 'pi pi-times-circle',
      acceptLabel: this.translate('Yes'),
      rejectLabel: this.translate('No'),
      acceptButtonStyleClass: action === 'approve' ? 'p-button-success' : 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      closeOnEscape: true,
      dismissableMask: true,
      accept: () => {
        // User confirmed, proceed with action
        this.processPermissionAction(permissionId, action, agreement, comments);
      },
      reject: () => {
        // User cancelled, do nothing
        this.messageService.add({
          severity: 'info',
          summary: this.translate('Cancelled'),
          detail: this.translate(`Permission request ${action} cancelled`),
          life: 3000
        });
      }
    });
  }

  private applyDefaultRequestType(): void {
    if (!this.requestForm || this.isViewMode) {
      return;
    }
    if (!this.defaultRequestType) {
      if (!this.lockRequestType) {
        this.requestForm.get('generalData.requestFor')?.enable({ emitEvent: false });
      }
      return;
    }
    const option = this.normalizeRequestType(this.defaultRequestType);
    if (!option) {
      return;
    }
    this.ensureRequestOptionExists(option);
    const control = this.requestForm.get('generalData.requestFor');
    control?.enable({ emitEvent: false });
    control?.setValue(option);
    this.updateTestEntriesForRequestType(option);
    if (this.lockRequestType) {
      control?.disable({ emitEvent: false });
    }
  }

  private applyContactPrefill(): void {
    if (!this.requestForm || this.isViewMode || !this.contactPrefill) {
      return;
    }
    if (this.contactPrefill.contactOfficer) {
      this.requestForm.get('contactOfficer')?.patchValue(this.contactPrefill.contactOfficer);
    }
    if (this.contactPrefill.questionMaker) {
      this.requestForm.get('questionMaker')?.patchValue(this.contactPrefill.questionMaker);
    }
    if (this.contactPrefill.generalData?.agencyOrManagement) {
      this.requestForm.get('generalData.agencyOrManagement')?.setValue(this.contactPrefill.generalData.agencyOrManagement);
    }
  }

  private enforceRequestTypeLock(): void {
    if (!this.requestForm || this.isViewMode) {
      return;
    }
    const control = this.requestForm.get('generalData.requestFor');
    if (this.lockRequestType && this.defaultRequestType) {
      control?.disable({ emitEvent: false });
    } else if (!this.lockRequestType) {
      control?.enable({ emitEvent: false });
    }
  }

  private normalizeRequestType(option: { value: string; name?: string } | string | null) {
    if (!option) {
      return null;
    }
    if (typeof option === 'string') {
      return {
        value: option,
        name: this.translate(option)
      };
    }
    if (!option.value) {
      return null;
    }
    return {
      name: option.name || this.translate(option.value),
      value: option.value
    };
  }

  private ensureRequestOptionExists(option: { value: string; name?: string }): void {
    if (!option?.value) {
      return;
    }
    if (!Array.isArray(this.permissionRequests)) {
      this.permissionRequests = [];
    }
    const exists = this.permissionRequests.some(item => item.value === option.value);
    if (!exists) {
      this.permissionRequests = [...this.permissionRequests, option];
    }
  }

  // New method to handle the actual approve/reject action
  private async processPermissionAction(permissionId: string, action: 'approve' | 'reject', agreement: boolean, comments: string): Promise<void> {
    try {
      if(action === 'reject') agreement = false;

      const endpoint = action === 'approve' ? 'permission-request/approve' : 'permission-request/reject';
      const payload = { 
        _id: permissionId,
        agreement: agreement,
        comments: comments
      };

      const response = await this.dataService.postData(endpoint, payload, 'PUT');
      
      if (!response.error) {
        // Disable edit mode and update form state
        this.isEditMode = false;
        
        // Update the permission status locally
        if (this.permission) {
          this.permission.status = action === 'approve' ? 'APPROVED' : 'REJECTED';
        }

        // Update form controls disabled state to reflect new status
        this.updateFormControlsDisabledState();

        this.messageService.add({
          severity: 'success',
          summary: this.translate('Success'),
          detail: this.translate(`Permission request ${action}d successfully`)
        });

        // Redirect to requests list
        this.router.navigate(['/permission/requests']);
        this.window.location.reload()
      } else {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate(`Failed to ${action} permission request`)
        });
      }
    } catch (error) {
      console.error(`Error ${action}ing permission:`, error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate(`An error occurred while ${action}ing the permission request`)
      });
    }
  }

  // Add cancelEdit method
  cancelEdit(): void {
    if (this.originalFormValue) {
      this.requestForm.patchValue(this.originalFormValue);
      this.requestForm.markAsPristine();
      this.requestForm.markAsUntouched();
    }
    this.isEditMode = false;
    this.updateFormControlsDisabledState();
  }

  // Add helper method to get enabled controls
  private getEnabledControls(formGroup: FormGroup | FormArray): AbstractControl[] {
    const enabledControls: AbstractControl[] = [];
    
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control) {
        if (control instanceof FormGroup || control instanceof FormArray) {
          enabledControls.push(...this.getEnabledControls(control));
        } else if (!control.disabled) {
          enabledControls.push(control);
        }
      }
    });
    
    return enabledControls;
  }

  // Add helper method to check if edit functionality should be available
  canEdit(): boolean {
    return this.permission?.status === 'PENDING';
  }

  // Add helper method to check if approve/reject functionality should be available
  canApproveReject(): boolean {
    return this.permission?.status === 'PENDING';
  }

  // Add helper method to get permission status for display
  getPermissionStatus(): string {
    return this.permission?.status || 'UNKNOWN';
  }

  // Add helper method to check if permission is approved
  isApproved(): boolean {
    return this.permission?.status === 'APPROVED';
  }

  // Add helper method to check if permission is rejected
  isRejected(): boolean {
    return this.permission?.status === 'REJECTED';
  }

  // Add helper method to check if permission is pending
  isPending(): boolean {
    return this.permission?.status === 'PENDING';
  }

  // Add public navigation method
  public navigateBack(): void {
    this.router.navigate(['/permission-requests']);
  }
}


